import { create } from "zustand"

export interface ChatMessage {
  id: string
  sender: string
  receiver: string
  content: string
  encrypted_content: string
  timestamp: string
  signature_valid: boolean
  isRead: boolean
  senderInContacts?: boolean
}

interface ChatStore {
  contacts: string[]
  activeChat: string | null
  messages: ChatMessage[]
  typingUsers: Set<string>
  addContact: (username: string) => void
  removeContact: (username: string) => void
  setActiveChat: (username: string | null) => void
  addMessage: (message: ChatMessage) => void
  setMessages: (messages: ChatMessage[]) => void
  setTyping: (username: string, isTyping: boolean) => void
  refreshContacts: (contacts: string[]) => void
}

export const useChatStore = create<ChatStore>((set) => ({
  contacts: [],
  activeChat: null,
  messages: [],
  typingUsers: new Set(),

  addContact: (username) =>
    set((state) => ({
      contacts: Array.from(new Set([...state.contacts, username])),
    })),

  removeContact: (username) =>
    set((state) => ({
      contacts: state.contacts.filter((c) => c !== username),
    })),

  setActiveChat: (username) =>
    set({
      activeChat: username,
      messages: [], // Clear messages when switching chats
    }),

  addMessage: (message) =>
    set((state) => {
      const exists = state.messages.some((m) => m.id === message.id)
      if (exists) return state

      return {
        messages: [...state.messages, message],
      }
    }),

  setMessages: (messages) => set({ messages }),

  setTyping: (username, isTyping) =>
    set((state) => {
      const newTypingUsers = new Set(state.typingUsers)
      if (isTyping) {
        newTypingUsers.add(username)
      } else {
        newTypingUsers.delete(username)
      }
      return { typingUsers: newTypingUsers }
    }),

  refreshContacts: (contacts) => set({ contacts }),
}))

export const isInContacts = (username: string): boolean => {
  return useChatStore.getState().contacts.includes(username)
}
